/**
 * CreatorLiquidStage — creator-side hero anchor.
 *
 * Mirrors the brand HeroLiquidStage in form (parallax layered glass panels,
 * same depth language, same liquid-glass primitives) but every panel carries
 * creator-personal data instead of brand/agency dashboard data:
 *
 *   - Big primary stat: "My earnings · this month"
 *   - Top-right: "Next payout" with arriving-in countdown
 *   - Top-left pill: "Latest clip · Trending today"
 *   - Floating bubble: per-1K view rate (CPM payout)
 *   - Bottom-left: "Today's verified views" mini bar chart on personal views
 *   - Bottom-right: "Open campaigns" — same real-data list that powers the
 *     brand stage, but framed as "briefs you can grab now" with a CPM column
 *     instead of a "recently added" pill, and a primary "Browse all" link.
 *
 * Personal metrics derive deterministically from the same liveMetrics
 * functions, scaled down to a realistic top-creator footprint
 * (~3M personal views/month cap → ~$7.5K/mo at $2.50 CPM).
 */
import * as React from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
  useReducedMotion,
  animate,
  type MotionValue,
} from "framer-motion";
import {
  Eye,
  Wallet,
  Zap,
  CircleDollarSign,
  Activity,
  Target,
  Flame,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { LiquidGlass } from "./LiquidGlass";
import {
  viewsAt,
  dailyCumulativeViews,
  msUntilNextHour,
  activeCampaigns,
  activeCampaignsFromList,
  type ActiveCampaign,
} from "./liveMetrics";
import { supabase } from "@/integrations/supabase/client";

/* ----------------------------------------------------------------- */
/* Personal scale — fraction of platform-wide views attributed to a   */
/* representative top creator. 0.054 yields ~1.2M views mid-May and   */
/* ~$3.0K earnings, which is realistic for a healthy creator.         */
/* The raw cap (~$7.5K/mo) is higher; mid-month shows ~half.          */
/* ----------------------------------------------------------------- */
const PERSONAL_VIEW_SHARE = 0.054;

/* ----------------------------------------------------------------- */
/* usePublicCampaigns — same pattern as the brand stage. Refetched    */
/* every hour so newly added campaigns appear without a redeploy.     */
/* ----------------------------------------------------------------- */
interface PublicCampaignRow {
  campaign_id: string;
  title: string;
  organization_name: string;
  cpm_rate: number | string | null;
  created_at: string;
}

function usePublicCampaigns(now: Date): {
  list: Array<{
    id: string;
    title: string;
    brand: string;
    cpm: number | null;
  }> | null;
  ready: boolean;
} {
  const [list, setList] = React.useState<
    Array<{ id: string; title: string; brand: string; cpm: number | null }>
    | null
  >(null);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await (
          supabase.rpc as unknown as (
            fn: string,
            args?: Record<string, unknown>
          ) => Promise<{ data: PublicCampaignRow[] | null; error: unknown }>
        )("get_public_marketing_campaigns", { _limit: 8 });
        if (cancelled) return;
        if (error || !data) {
          setList(null);
        } else {
          const isOwnBrand = (s: string) => {
            const v = (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
            return v.includes("creatorvault");
          };
          const filtered = data.filter(
            (r) =>
              !isOwnBrand(r.organization_name || "") &&
              !isOwnBrand(r.title || "")
          );
          setList(
            filtered.map((r) => ({
              id: r.campaign_id,
              title: r.title,
              brand: r.organization_name || "",
              cpm: r.cpm_rate == null ? null : Number(r.cpm_rate),
            }))
          );
        }
      } catch {
        if (!cancelled) setList(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [now]);

  return { list, ready };
}

/* ----------------------------------------------------------------- */
/* useCreatorMetrics — re-evaluates every hour at the top of the hour */
/* ----------------------------------------------------------------- */
function useCreatorMetrics() {
  const [now, setNow] = React.useState<Date>(() => new Date());
  React.useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    let interval: ReturnType<typeof setInterval>;
    function tick() {
      setNow(new Date());
      interval = setInterval(() => setNow(new Date()), 60 * 60 * 1000);
    }
    timeout = setTimeout(tick, msUntilNextHour());
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  const { list: realList, ready } = usePublicCampaigns(now);

  return React.useMemo(() => {
    const m = viewsAt(now);
    const sparkPlatform = dailyCumulativeViews(now);
    // Scale to personal footprint
    const myViews = Math.round(m.views * PERSONAL_VIEW_SHARE);
    const myEarnings = Math.round((myViews / 1000) * 2.5 * 100) / 100;
    const myDailyViews = sparkPlatform.map((v) =>
      Math.round(v * PERSONAL_VIEW_SHARE)
    );

    // Next payout = sum of last 7 days of personal earnings, capped at $1.5K.
    // Lands cleanly because it's deterministic from the spark series.
    const last7 = myDailyViews.slice(-7);
    const last7Sum =
      last7.length >= 2
        ? Math.max(0, last7[last7.length - 1] - last7[0])
        : Math.round(myViews * 0.18);
    const nextPayout = Math.round((last7Sum / 1000) * 2.5 * 100) / 100;

    // Days until payday — deterministic mod-7 from the date
    const dayUtc = now.getUTCDate();
    const daysToPayout = ((7 - (dayUtc % 7)) % 7) || 3;

    const realCampaigns =
      realList && realList.length > 0
        ? activeCampaignsFromList(now, realList.slice(0, 4))
        : null;
    const campaigns = realCampaigns ?? activeCampaigns(now, 4);
    const realCpms = realList?.filter((r) => r.cpm != null).map((r) => r.cpm as number) ?? [];

    return {
      ...m,
      myViews,
      myEarnings,
      nextPayout,
      daysToPayout,
      sparkPersonalDaily: myDailyViews,
      campaigns,
      realCpms,
      now,
    };
  }, [now, realList, ready]);
}

/* ----------------------------------------------------------------- */
/* Hooks                                                               */
/* ----------------------------------------------------------------- */
function useMouseParallax(ref: React.RefObject<HTMLDivElement>) {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 80, damping: 16, mass: 0.5 });
  const sy = useSpring(my, { stiffness: 80, damping: 16, mass: 0.5 });

  React.useEffect(() => {
    if (reduce) return;
    const node = ref.current;
    if (!node) return;
    function handle(e: MouseEvent) {
      const r = node!.getBoundingClientRect();
      mx.set((e.clientX - r.left) / r.width - 0.5);
      my.set((e.clientY - r.top) / r.height - 0.5);
    }
    function leave() {
      mx.set(0);
      my.set(0);
    }
    node.addEventListener("mousemove", handle);
    node.addEventListener("mouseleave", leave);
    return () => {
      node.removeEventListener("mousemove", handle);
      node.removeEventListener("mouseleave", leave);
    };
  }, [ref, reduce, mx, my]);

  return { sx, sy, reduce };
}

/* ----------------------------------------------------------------- */
/* Counter                                                             */
/* ----------------------------------------------------------------- */
function Counter({
  to,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 1.8,
  short = false,
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  duration?: number;
  short?: boolean;
}) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const reduce = useReducedMotion();

  const fmt = React.useCallback(
    (v: number) => {
      if (short) {
        if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
        if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
      }
      return v.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    [short, decimals]
  );

  React.useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.textContent = `${prefix}${fmt(to)}${suffix}`;
  }, [to, prefix, suffix, fmt]);

  React.useEffect(() => {
    if (!ref.current || reduce) return;
    const node = ref.current;
    const ctrl = animate(0, to, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        node.textContent = `${prefix}${fmt(v)}${suffix}`;
      },
      onComplete: () => {
        node.textContent = `${prefix}${fmt(to)}${suffix}`;
      },
    });
    return () => ctrl.stop();
  }, [to, prefix, suffix, duration, fmt, reduce]);

  return <span ref={ref}>{`${prefix}${fmt(to)}${suffix}`}</span>;
}

/* ----------------------------------------------------------------- */
/* Sparkline                                                           */
/* ----------------------------------------------------------------- */
function Sparkline({
  values,
  color = "var(--rd-accent-500)",
  tall = false,
}: {
  values: number[];
  color?: string;
  tall?: boolean;
}) {
  const w = 100;
  const h = tall ? 88 : 32;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const d = `M ${points.replace(/ /g, " L ")}`;
  const id = React.useId().replace(/[:]/g, "");

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={tall ? "h-full w-full" : "h-8 w-full"}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={`fill-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={`hsl(${color})`} stopOpacity="0.42" />
          <stop offset="100%" stopColor={`hsl(${color})`} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`stroke-${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={`hsl(${color})`} stopOpacity="0.7" />
          <stop offset="100%" stopColor={`hsl(${color})`} stopOpacity="1" />
        </linearGradient>
      </defs>
      <motion.path
        d={`${d} L ${w},${h} L 0,${h} Z`}
        fill={`url(#fill-${id})`}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.6 }}
      />
      <motion.path
        d={d}
        fill="none"
        stroke={`url(#stroke-${id})`}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
      />
    </svg>
  );
}

/* ----------------------------------------------------------------- */
/* OpenCampaignsCard — creator-framing of the active campaigns list   */
/* Shows brand · brief title · CPM rate · "Open" pill + a Browse-all  */
/* link to /campaigns. Uses the same real-data shape but reframes for */
/* a creator who's looking for paid briefs to grab.                    */
/* ----------------------------------------------------------------- */
function OpenCampaignsCard({
  campaigns,
  realCpms,
  delayBase = 0.5,
}: {
  campaigns: ActiveCampaign[];
  realCpms: number[];
  delayBase?: number;
}) {
  // Pair each campaign with a stable CPM. Prefer real CPMs from the API;
  // otherwise synthesize $2.00 – $4.50 in $0.50 steps based on the campaign id
  // so values are stable across renders.
  function cpmFor(idx: number, id: string): number {
    if (realCpms[idx] != null) return realCpms[idx]!;
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    const steps = [2.0, 2.5, 3.0, 3.5, 4.0, 4.5];
    return steps[h % steps.length];
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--rd-text-3))]">
          <Target className="h-3 w-3" /> Open campaigns
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--rd-success))]/15 px-2 py-0.5 text-[10.5px] font-medium text-[hsl(var(--rd-success))]">
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--rd-success))]/60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--rd-success))]" />
          </span>
          Live
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {campaigns.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.55,
              ease: [0.16, 1, 0.3, 1],
              delay: delayBase + i * 0.1,
            }}
            className="flex items-center gap-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5">
                <span className="truncate text-[12px] font-medium text-[hsl(var(--rd-text-1))]">
                  {c.name}
                </span>
                <span className="shrink-0 text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--rd-text-3))]">
                  · {c.brand}
                </span>
              </div>
            </div>
            <div className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[hsl(var(--rd-accent-500))]/35 bg-[hsl(var(--rd-accent-500))]/10 px-2 py-0.5 font-mono text-[10.5px] font-medium tabular-nums text-[hsl(var(--rd-accent-200))]">
              ${cpmFor(i, c.id).toFixed(2)} <span className="text-[hsl(var(--rd-accent-300))]/70">/1K</span>
            </div>
          </motion.div>
        ))}
      </div>
      <Link
        to="/auth?type=creator"
        className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-[hsl(var(--rd-accent-300))] transition hover:text-[hsl(var(--rd-accent-200))]"
      >
        Browse all open briefs <ArrowRight className="h-3 w-3" />
      </Link>
    </>
  );
}

/* ----------------------------------------------------------------- */
/* FloatPanel                                                          */
/* ----------------------------------------------------------------- */
function FloatPanel({
  className,
  style,
  parallaxX,
  parallaxY,
  rotate = 0,
  delay = 0,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  parallaxX: MotionValue<number>;
  parallaxY: MotionValue<number>;
  rotate?: number;
  delay?: number;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      className={`absolute ${className ?? ""}`}
      style={{ x: parallaxX, y: parallaxY, rotate, ...style }}
      initial={{ opacity: 0, y: 24, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

/* ----------------------------------------------------------------- */
/* MobileStack — vertical layout for narrow screens                   */
/* ----------------------------------------------------------------- */
function MobileStack({
  metrics,
}: {
  metrics: ReturnType<typeof useCreatorMetrics>;
}) {
  const { myEarnings, myViews, monthLabel, dateLabel, nextPayout, daysToPayout } =
    metrics;
  const sparkVals =
    metrics.sparkPersonalDaily.length >= 2
      ? metrics.sparkPersonalDaily
      : [0, myViews];
  return (
    <div className="flex flex-col gap-3 md:hidden">
      <LiquidGlass radius={20} elevation="high" className="px-5 py-5">
        <div className="flex items-center justify-between gap-2 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--rd-text-3))]">
          <span className="flex items-center gap-2">
            <CircleDollarSign className="h-3.5 w-3.5" /> My earnings · this month
          </span>
          <span className="normal-case tracking-normal text-[10px] text-[hsl(var(--rd-text-3))]">
            {monthLabel}
          </span>
        </div>
        <div className="mt-2 flex items-end justify-between gap-3">
          <div className="font-mono text-[36px] font-semibold leading-none tabular-nums text-[hsl(var(--rd-text-1))]">
            <Counter
              to={myEarnings}
              prefix="$"
              decimals={2}
              key={`me-${Math.round(myEarnings)}`}
            />
          </div>
          <div className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--rd-success))]/15 px-2 py-0.5 text-[10.5px] font-medium text-[hsl(var(--rd-success))]">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--rd-success))]/60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--rd-success))]" />
            </span>
            Live
          </div>
        </div>
        <div className="mt-3">
          <Sparkline values={sparkVals} />
        </div>
      </LiquidGlass>

      <div className="grid grid-cols-2 gap-3">
        <LiquidGlass radius={18} elevation="mid" className="px-4 py-4">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--rd-text-3))]">
            <Wallet className="h-3 w-3" /> Next payout
          </div>
          <div className="mt-1.5 font-mono text-[20px] font-semibold tabular-nums text-[hsl(var(--rd-text-1))]">
            <Counter
              to={nextPayout}
              prefix="$"
              decimals={2}
              key={`np-${Math.round(nextPayout)}`}
            />
          </div>
          <div className="mt-0.5 text-[10px] text-[hsl(var(--rd-text-3))]">
            Arrives in {daysToPayout} {daysToPayout === 1 ? "day" : "days"}
          </div>
        </LiquidGlass>
        <LiquidGlass radius={18} elevation="mid" className="px-4 py-4">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--rd-text-3))]">
            <Eye className="h-3 w-3" /> My verified views
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <div className="font-mono text-[22px] font-semibold tabular-nums text-[hsl(var(--rd-text-1))]">
              <Counter to={myViews} short />
            </div>
          </div>
          <div className="mt-0.5 text-[10px] text-[hsl(var(--rd-text-3))]">
            {dateLabel}
          </div>
        </LiquidGlass>
      </div>

      <LiquidGlass radius={18} elevation="high" className="px-5 py-4">
        <OpenCampaignsCard
          campaigns={metrics.campaigns}
          realCpms={metrics.realCpms}
          delayBase={0.5}
        />
      </LiquidGlass>
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Main creator stage                                                  */
/* ----------------------------------------------------------------- */
export function CreatorLiquidStage() {
  const stageRef = React.useRef<HTMLDivElement>(null);
  const { sx, sy, reduce } = useMouseParallax(stageRef);
  const metrics = useCreatorMetrics();

  const { scrollYProgress } = useScroll({
    target: stageRef,
    offset: ["start end", "end start"],
  });
  const scrollLift = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const px = (mult: number) =>
    useTransform(sx, (v) => (reduce ? 0 : v * mult));
  const py = (mult: number) =>
    useTransform(sy, (v) => (reduce ? 0 : v * mult));

  const backY = useTransform(
    [py(8), scrollLift] as unknown as MotionValue<number>[],
    (vals) => {
      const arr = vals as unknown as number[];
      return arr[0] + arr[1];
    }
  );

  // Build per-day deltas for the bottom-left bar chart (creator personal views)
  const sv = metrics.sparkPersonalDaily;
  const todayViews =
    sv.length >= 1 ? sv[sv.length - 1] - (sv.length >= 2 ? sv[sv.length - 2] : 0) : 0;
  const deltas: number[] = [];
  for (let i = 1; i < sv.length; i++) deltas.push(Math.max(0, sv[i] - sv[i - 1]));
  const recent = deltas.slice(-12);
  while (recent.length < 12) recent.unshift(0);
  const max = Math.max(1, ...recent);
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(2)}M`
      : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}K`
      : `${n}`;

  return (
    <>
      <MobileStack metrics={metrics} />
      <div
        ref={stageRef}
        className="relative hidden h-[680px] w-full select-none [perspective:1400px] md:block"
      >
        {/* ============= LATEST CLIP STATUS PILL (top-left) ============= */}
        <FloatPanel
          className="left-[2%] top-[2%]"
          parallaxX={px(-18)}
          parallaxY={py(-10)}
          delay={0.0}
        >
          <LiquidGlass
            radius={999}
            elevation="low"
            className="px-3.5 py-1.5"
            noChroma
          >
            <div className="flex items-center gap-2 text-[11px] font-medium text-[hsl(var(--rd-text-2))]">
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--rd-accent-400))]/70" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--rd-accent-400))]" />
              </span>
              <Flame className="h-3 w-3 text-[hsl(var(--rd-accent-300))]" />
              Latest clip ·{" "}
              <span className="text-[hsl(var(--rd-text-1))]">
                Trending today
              </span>
            </div>
          </LiquidGlass>
        </FloatPanel>

        {/* ============= MY EARNINGS · THIS MONTH (left, hero) ============= */}
        <FloatPanel
          className="left-[2%] top-[12%] w-[52%]"
          parallaxX={px(-10)}
          parallaxY={backY as MotionValue<number>}
          delay={0.05}
        >
          <LiquidGlass radius={22} elevation="high" className="px-7 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--rd-text-3))]">
                <CircleDollarSign className="h-3.5 w-3.5" />
                My earnings · this month
              </div>
              <div className="text-[11px] tracking-normal text-[hsl(var(--rd-text-3))] normal-case">
                {metrics.monthLabel}
              </div>
            </div>
            <div className="mt-4 flex items-end gap-3">
              <div className="font-mono text-[44px] font-semibold leading-none tabular-nums text-[hsl(var(--rd-text-1))] md:text-[58px]">
                <Counter
                  to={metrics.myEarnings}
                  prefix="$"
                  decimals={2}
                  key={`me-${Math.round(metrics.myEarnings)}`}
                />
              </div>
              <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--rd-success))]/15 px-2 py-0.5 text-[12px] font-medium text-[hsl(var(--rd-success))]">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--rd-success))]/60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--rd-success))]" />
                </span>
                Live
              </div>
            </div>
            <div className="mt-1 text-[11.5px] text-[hsl(var(--rd-text-3))]">
              {fmt(metrics.myViews)} verified views · paid at $2.50 / 1K
            </div>
            <div className="mt-5 h-[88px]">
              <Sparkline
                values={
                  metrics.sparkPersonalDaily.length >= 2
                    ? metrics.sparkPersonalDaily
                    : [0, metrics.myViews]
                }
                tall
              />
            </div>
            <div className="mt-4 flex items-center justify-between text-[11px] text-[hsl(var(--rd-text-3))]">
              <span>{metrics.monthLabel.split(" ")[0]} 1</span>
              <span>Mid-{metrics.monthLabel.split(" ")[0]}</span>
              <span className="text-[hsl(var(--rd-text-2))]">
                {metrics.dateLabel}
              </span>
            </div>
          </LiquidGlass>
        </FloatPanel>

        {/* ============= NEXT PAYOUT (top-right) ============= */}
        <FloatPanel
          className="right-[2%] top-[4%] w-[38%]"
          parallaxX={px(16)}
          parallaxY={py(-12)}
          delay={0.18}
        >
          <LiquidGlass radius={22} elevation="mid" className="px-5 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--rd-text-3))]">
                <Wallet className="h-3.5 w-3.5" />
                Next payout
              </div>
              <div className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--rd-success))]/15 px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--rd-success))]">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--rd-success))]/60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--rd-success))]" />
                </span>
                Scheduled
              </div>
            </div>
            <div className="mt-2 font-mono text-[32px] font-semibold tabular-nums text-[hsl(var(--rd-text-1))]">
              <Counter
                to={metrics.nextPayout}
                prefix="$"
                decimals={2}
                key={`np-${Math.round(metrics.nextPayout)}`}
              />
            </div>
            <div className="mt-1 text-[11.5px] text-[hsl(var(--rd-text-2))]">
              Arrives in {metrics.daysToPayout}{" "}
              {metrics.daysToPayout === 1 ? "day" : "days"} · ACH or stablecoin
            </div>
            {/* Method context bar */}
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2">
              <div>
                <div className="text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--rd-text-3))]">
                  My CPM rate
                </div>
                <div className="font-mono text-[14px] font-semibold tabular-nums text-[hsl(var(--rd-text-1))]">
                  $2.50
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--rd-text-3))]">
                  Cycle
                </div>
                <div className="font-mono text-[12px] font-medium tabular-nums text-[hsl(var(--rd-text-2))]">
                  Weekly
                </div>
              </div>
            </div>
          </LiquidGlass>
        </FloatPanel>

        {/* ============= PER-1K BUBBLE (anchored under hero) ============= */}
        <FloatPanel
          className="left-[36%] top-[57%]"
          parallaxX={px(24)}
          parallaxY={py(-22)}
          rotate={-3}
          delay={0.45}
        >
          <LiquidGlass radius={999} elevation="high" className="px-4 py-2.5">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-[hsl(var(--rd-accent-300))]" />
              <div className="font-mono text-[14px] font-semibold tabular-nums text-[hsl(var(--rd-text-1))]">
                $2.50
              </div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--rd-text-3))]">
                / 1K views
              </div>
            </div>
          </LiquidGlass>
        </FloatPanel>

        {/* ============= TODAY'S VIEWS (bottom-left) ============= */}
        <FloatPanel
          className="left-[3%] bottom-[3%] w-[32%]"
          parallaxX={px(-20)}
          parallaxY={py(16)}
          delay={0.32}
        >
          <LiquidGlass radius={20} elevation="mid" className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--rd-text-3))]">
                <Zap className="h-3.5 w-3.5" />
                Today’s views · my clips
              </div>
              <div className="text-[10.5px] text-[hsl(var(--rd-text-3))]">
                {metrics.dateLabel}
              </div>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-mono text-[26px] font-semibold tabular-nums text-[hsl(var(--rd-text-1))]">
                {fmt(todayViews)}
              </div>
              <div className="text-[11px] text-[hsl(var(--rd-text-3))]">
                verified
              </div>
            </div>
            <div className="mt-3 flex h-[32px] items-end gap-[3px]">
              {recent.map((v, i) => (
                <motion.span
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${max > 0 ? (v / max) * 100 : 0}%` }}
                  transition={{
                    duration: 0.7,
                    delay: 0.5 + i * 0.04,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="flex-1 rounded-[2px]"
                  style={{
                    background:
                      i === recent.length - 1
                        ? "linear-gradient(180deg, hsl(var(--rd-accent-300)) 0%, hsl(var(--rd-accent-500)) 100%)"
                        : "linear-gradient(180deg, hsl(var(--rd-accent-400) / 0.7) 0%, hsl(var(--rd-accent-700) / 0.35) 100%)",
                  }}
                />
              ))}
            </div>
            <div className="mt-1.5 text-[10.5px] text-[hsl(var(--rd-text-3))]">
              Last 12 days · today highlighted
            </div>
          </LiquidGlass>
        </FloatPanel>

        {/* ============= OPEN CAMPAIGNS (bottom-right) ============= */}
        <FloatPanel
          className="right-[2%] bottom-[3%] w-[44%]"
          parallaxX={px(14)}
          parallaxY={py(18)}
          delay={0.4}
        >
          <LiquidGlass radius={20} elevation="high" className="px-5 py-4">
            <OpenCampaignsCard
              campaigns={metrics.campaigns}
              realCpms={metrics.realCpms}
              delayBase={0.7}
            />
          </LiquidGlass>
        </FloatPanel>
      </div>
    </>
  );
}
